import { AnimatePresence, motion } from "motion/react";
import type { MouseEventHandler, ReactNode } from "react";
import {
  motionTransition,
  motionVariants,
  type MotionSurface,
  useReducedMotionSafe
} from "./motion";

type MotionPresenceProps = {
  show: boolean;
  children?: ReactNode;
};

type MotionSwitchProps = {
  motionKey: string;
  className?: string;
  children?: ReactNode;
};

function MotionSwitch({
  motionKey,
  className,
  surface,
  children
}: MotionSwitchProps & { surface: MotionSurface }) {
  const reduceMotion = useReducedMotionSafe();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={motionKey}
        className={className}
        initial="initial"
        animate="animate"
        exit="exit"
        variants={motionVariants[surface]}
        transition={motionTransition(surface, reduceMotion)}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

function MotionKeyed({
  motionKey,
  className,
  surface,
  children
}: MotionSwitchProps & { surface: MotionSurface }) {
  const reduceMotion = useReducedMotionSafe();

  return (
    <motion.div
      key={motionKey}
      className={className}
      initial="initial"
      animate="animate"
      variants={motionVariants[surface]}
      transition={motionTransition(surface, reduceMotion)}
    >
      {children}
    </motion.div>
  );
}

export function MotionRoute({
  routeKey,
  children
}: {
  routeKey: string;
  children?: ReactNode;
}) {
  return (
    <MotionSwitch
      motionKey={routeKey}
      className="app-route-motion"
      surface="routeFade"
    >
      {children}
    </MotionSwitch>
  );
}

export function MotionFadeThrough({
  motionKey,
  className,
  children
}: MotionSwitchProps) {
  return (
    <MotionKeyed
      motionKey={motionKey}
      className={className}
      surface="fadeThrough"
    >
      {children}
    </MotionKeyed>
  );
}

export function MotionContainerOpen({
  motionKey,
  className,
  children
}: MotionSwitchProps) {
  return (
    <MotionKeyed
      motionKey={motionKey}
      className={className}
      surface="containerOpen"
    >
      {children}
    </MotionKeyed>
  );
}

export function MotionSharedAxis({
  motionKey,
  className,
  axis = "x",
  direction = 1,
  children
}: MotionSwitchProps & { axis?: "x" | "y"; direction?: 1 | -1 }) {
  const reduceMotion = useReducedMotionSafe();
  const enterOffset = reduceMotion ? 0 : axis === "x" ? 16 : 10;
  const exitOffset = reduceMotion ? 0 : axis === "x" ? -12 : -8;
  const initial =
    axis === "x"
      ? { opacity: 1, x: enterOffset * direction }
      : { opacity: 1, y: enterOffset * direction };
  const exit =
    axis === "x"
      ? { opacity: 0, x: exitOffset * direction }
      : { opacity: 0, y: exitOffset * direction };

  return (
    <motion.div
      key={motionKey}
      className={className}
      initial={initial}
      animate={{ opacity: 1, x: 0, y: 0 }}
      exit={exit}
      transition={motionTransition("fadeThrough", reduceMotion)}
    >
      {children}
    </motion.div>
  );
}

export function MotionFade({
  show,
  as = "div",
  ariaLabel,
  className,
  onClick,
  tabIndex,
  children
}: MotionPresenceProps & {
  as?: "div" | "button";
  ariaLabel?: string;
  className?: string;
  onClick?: () => void;
  tabIndex?: number;
}) {
  const reduceMotion = useReducedMotionSafe();
  const motionProps = {
    className,
    initial: "initial",
    animate: "animate",
    exit: "exit",
    variants: motionVariants.fade,
    transition: motionTransition("fade", reduceMotion)
  };

  return (
    <AnimatePresence>
      {show ? (
        as === "button" ? (
          <motion.button
            {...motionProps}
            type="button"
            aria-label={ariaLabel}
            tabIndex={tabIndex}
            onClick={onClick}
          >
            {children}
          </motion.button>
        ) : (
          <motion.div {...motionProps}>{children}</motion.div>
        )
      ) : null}
    </AnimatePresence>
  );
}

export function MotionPopover({
  show,
  id,
  className,
  children
}: MotionPresenceProps & { id?: string; className?: string }) {
  const reduceMotion = useReducedMotionSafe();

  return (
    <AnimatePresence>
      {show ? (
        <motion.div
          id={id}
          className={className}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={motionVariants.popover}
          transition={motionTransition("popover", reduceMotion)}
        >
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export function MotionDrawer({
  show,
  id,
  className,
  ariaLabel,
  children
}: MotionPresenceProps & {
  id?: string;
  className?: string;
  ariaLabel: string;
}) {
  const reduceMotion = useReducedMotionSafe();

  return (
    <AnimatePresence>
      {show ? (
        <motion.aside
          id={id}
          className={`${className ?? ""} is-open`.trim()}
          aria-label={ariaLabel}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={motionVariants.drawer}
          transition={motionTransition("drawer", reduceMotion)}
        >
          {children}
        </motion.aside>
      ) : null}
    </AnimatePresence>
  );
}

export function MotionModal({
  show,
  className,
  panelClassName,
  role = "dialog",
  labelledBy,
  onMouseDown,
  children
}: MotionPresenceProps & {
  className: string;
  panelClassName: string;
  role?: "dialog" | "alertdialog";
  labelledBy: string;
  onMouseDown?: MouseEventHandler<HTMLDivElement>;
}) {
  const reduceMotion = useReducedMotionSafe();

  return (
    <AnimatePresence>
      {show ? (
        <motion.div
          className={className}
          onMouseDown={onMouseDown}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={motionVariants.fade}
          transition={motionTransition("fade", reduceMotion)}
        >
          <motion.section
            className={panelClassName}
            role={role}
            aria-modal="true"
            aria-labelledby={labelledBy}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={motionVariants.modal}
            transition={motionTransition("modal", reduceMotion)}
          >
            {children}
          </motion.section>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export function MotionBottomBar({
  show,
  className,
  ariaLabel,
  children
}: MotionPresenceProps & { className?: string; ariaLabel: string }) {
  const reduceMotion = useReducedMotionSafe();

  return (
    <AnimatePresence>
      {show ? (
        <motion.div
          className={className}
          aria-label={ariaLabel}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={motionVariants.bottomBar}
          transition={motionTransition("bottomBar", reduceMotion)}
        >
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
